import MessageSchema, { type MessageJSON } from "messaging/_schemas/message-schema";
import { z } from "zod";

/**
 * The wire shape of one entry from `POST messaging/bulk-messages`.
 *
 * The endpoint answers 200 for the batch as a whole and reports per-message outcomes inside it, so a
 * single response array carries two different shapes. A success is an ordinary message. A failure is
 * not one — there is no saved message behind it — so it carries only which message failed and why.
 *
 * Parsing the whole array as messages is what turned a reportable failure into an unhandled ZodError.
 * The throw discarded `issues` — the only place the server says what actually went wrong — rejected
 * the mutation, and left the client re-saving a batch that could never succeed. Modelling the failure
 * as its own shape is what makes the server's own error message reachable.
 *
 * A server predating that cleanup built the failure entry from the REQUEST instead, so it arrives
 * carrying `created` and `forum.createdDate` at .NET's `default(DateTime)` ("0001-01-01T00:00:00", no
 * timezone), `forum.entityClass` null, and a placeholder forum. Both shapes are read, so a rolling
 * deployment reports the failure either way rather than throwing on the older one.
 */
export const BulkMessageFailureZ = z.object({
  id: z.uuid().nullish(),
  /** Why this one message was not saved, e.g. "Message id is required." */
  issues: z.array(z.string()).nonempty(),
});

export type BulkMessageFailure = z.infer<typeof BulkMessageFailureZ>;

/**
 * Failure first, deliberately: a successful entry carries `issues: null`, so it cannot match the
 * failure branch and falls through to the message shape. Ordering these the other way would test a
 * malformed failure against the message schema first and reintroduce the very throw this replaces.
 */
export const BulkMessageResponseZ = z.union([BulkMessageFailureZ, MessageSchema.MessageJSONZ]);

export type BulkMessageResponse = z.infer<typeof BulkMessageResponseZ>;

export function isBulkMessageFailure(entry: BulkMessageResponse): entry is BulkMessageFailure {
  const issues = (entry as { issues?: unknown }).issues;
  return Array.isArray(issues) && issues.length > 0;
}

/** The complement of {@link isBulkMessageFailure}: anything that parsed as a real saved message. */
export function isBulkMessageSuccess(entry: BulkMessageResponse): entry is MessageJSON {
  return !isBulkMessageFailure(entry);
}

/** Every issue the batch reported, flattened, so a partial failure can be stated in one sentence. */
export function bulkMessageIssues(entries: readonly BulkMessageResponse[]): string[] {
  return entries.filter((entry) => isBulkMessageFailure(entry)).flatMap((entry) => entry.issues);
}
